import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@design-system/lib/utils';

const alertVariants = cva(
  'relative w-full rounded-lg border p-4 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5',
  {
    variants: {
      variant: {
        default: 'bg-muted text-card-foreground [&>svg]:text-gray-950',
        destructive:
          'text-destructive bg-destructive/5 border-destructive/10 [&>svg]:text-destructive *:data-[slot=alert-description]:text-destructive/80 *:data-[slot=alert-title]:text-destructive',
        success:
          'text-green-600 bg-green-600/5 border-green-600/20 [&>svg]:text-green-800 *:data-[slot=alert-description]:text-green-800/80 *:data-[slot=alert-title]:text-green-800',
        warning:
          'text-yellow-600 bg-yellow-600/5 border-yellow-600/20 [&>svg]:text-yellow-800 *:data-[slot=alert-description]:text-yellow-800/80 *:data-[slot=alert-title]:text-yellow-800',
        info: 'text-primary-dark bg-primary/5 border-primary/10 [&>svg]:text-primary-dark *:data-[slot=alert-description]:text-primary-dark *:data-[slot=alert-title]:text-primary-dark',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        'col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight text-gray-950',
        className,
      )}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'text-foreground col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed',
        className,
      )}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription };
